import { useMemo } from 'react';

import { useCohortVersionQuery } from '@verantos-dev/data-access-cohorts';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import type { UseInheritedStudyPeriodResult } from '../types';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisSourceCohort } from './useAnalysisSourceCohort';

// Legacy cohort versions sometimes wire `jsonValue` as a JSON string even
// though the type says object — parse defensively.
const parseCohortDefinition = (
  raw: CohortBuilderDefinitionLegacy | string | undefined
): CohortBuilderDefinitionLegacy | undefined => {
  if (!raw) return undefined;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw) as CohortBuilderDefinitionLegacy;
  } catch {
    return undefined;
  }
};

/**
 * Resolves the study period the analysis inherits from its first resolved
 * cohort source (the cohort version's overall study-period restriction) —
 * an analysis has no study period of its own.
 */
export const useInheritedStudyPeriod = (): UseInheritedStudyPeriodResult => {
  const { projectId } = useAnalysisRouteParams();
  const {
    cohortProfileId,
    selectedCohort,
    isLoading: isSourceCohortLoading,
  } = useAnalysisSourceCohort();
  const cohortVersionId = selectedCohort?.latestCohortVersionId ?? '';
  const inheritedFromCohortName = selectedCohort?.cohortName ?? null;

  // `cohortId` param actually takes the profile id — naming is inconsistent
  // across the codebase but the endpoint accepts it.
  const { data: versionData, isLoading: isVersionLoading } = useCohortVersionQuery({
    projectId,
    cohortId: cohortProfileId ?? '',
    cohortVersionId,
    enabled: Boolean(projectId && cohortProfileId && cohortVersionId),
  });

  const definition = useMemo(
    () => parseCohortDefinition(versionData?.jsonValue),
    [versionData?.jsonValue]
  );
  const studyPeriod = definition?.studyPeriodRestrictions?.overallStudyPeriodRestriction;
  const startDate = studyPeriod?.startDate ?? null;
  const endDate = studyPeriod?.endDate ?? null;
  const isSet = Boolean(startDate && endDate);

  const isLoading = isSourceCohortLoading || (Boolean(cohortVersionId) && isVersionLoading);

  return { startDate, endDate, isSet, inheritedFromCohortName, isLoading };
};
